import {Component, HostBinding, Input, OnDestroy, OnInit} from '@angular/core';
import {Event} from '@api/models/event/event';
import {Team} from '@api/models/team/team';
import {LinkService} from '@core/services/link.service';
import {ImageSubType} from '@api/models/image/image-type';
import {IPicture} from '@api/models/image/picture';
import {ContentCardLayout} from '@lib-modules/cards/components/content-card/content-card.component';
import {LivestreamStatus} from '@api/models/livestream/streaming-info';
import {LibUtilsService} from '@utils/services/lib-utils.service';
import {SportTypeService} from '@api/services/sport-type/sport-type.service';
import {SportType} from '@lib/models/sport-type/sport-type';
import {HomeRoutes} from '@app/routes/home.routes';
import {ColorScheme} from '@lib/models/color-scheme/color-scheme';
import {MatchPollingService, MatchPollUpdateType} from '@lib-modules/match/services/match-polling/match-polling.service';
import {Subscription} from 'rxjs';
import {VideoSubType} from '@api/models/video/video';
import {MatchRoutePaths} from '@lib-modules/match/match-route-paths';

@Component({
  selector: 'lib-match-card',
  templateUrl: './match-card.component.html',
  styleUrls: ['./match-card.component.scss'],
})
export class LibMatchCardComponent implements OnInit, OnDestroy {
  @Input() public match: Event;
  @Input() public colorScheme = ColorScheme.Default;
  @Input() public orientation: 'landscape' | 'portrait' = 'portrait';
  @Input() public showContent = true;
  @HostBinding('class.download-card') @Input() public showDownloads = false;
  @Input() public layout: ContentCardLayout;
  @Input() public lazyLoadImage = true;
  @Input() public showStatusBadge = true;
  @Input() public path: string;
  @Input() public link: string;
  @Input() public live = false;
  @Input() public sportType: SportType;
  protected readonly LivestreamStatus = LivestreamStatus;
  public title: string;
  public homeTeam: Team;
  public awayTeam: Team;
  public homeScore: string;
  public awayScore: string;
  public picture: IPicture;
  public shortDateString: string;
  public timeString: string;
  public minute: string;
  public score: string;
  public liveStreamStatus: string;
  public minimalLiveStreamStatus: string;
  public liveStreamStatusColor: string;
  public minimalLayout = ContentCardLayout.Minimal;
  public highlightVideoDownload: string;
  public replayVideoDownload: string;
  private poll$: Subscription;

  constructor(private matchPollingService: MatchPollingService) {
  }

  public async ngOnInit(): Promise<void> {
    if (!this.sportType) {
      this.sportType = SportTypeService.selectedSportType;
    }
    this.match = new Event(this.match);
    if (this.match.pictures?.length > 0) {
      const imageOrientation =
        this.orientation === 'landscape'
          ? ImageSubType.THUMBNAIL_LANDSCAPE
          : ImageSubType.THUMBNAIL_SQUARE;
      this.picture = this.match.pictures.find(
        (p) => p.subtype === imageOrientation
      );
    }

    this.homeTeam = this.match.teams.home;
    this.awayTeam = this.match.teams.away;
    this.title = this.homeTeam.name + ' vs. ' + this.awayTeam.name
    if (this.match.isPreGame() && !this.match.willStartSoon()) {
      this.path = MatchRoutePaths.Overview;
    }
    if (this.sportType === SportType.Football) {
      const matchLink = LinkService.getLinkToMatch(
        this.match._id,
        this.match.teams.home.teamid
      );
      this.link = `${matchLink}/${this.path || ''}`;
    }
    if (this.sportType === SportType.Handball) {
      this.link = `/${HomeRoutes.handball.relative.videos}/${this.match._id}`;
      const score = this.match.scores?.ui_scores?.main_score
      const subScore = this.match.scores?.ui_scores?.sub_score;
      this.title += (score ? ` | ${score}` : '');
      this.title += (subScore ? ` (${subScore})` : '');
    }
    this.setLiveStreamStatus();
    this.setDate();
    this.startPolling();
    this.setScore();
    this.setVideos();
  }

  public ngOnDestroy(): void {
    this.matchPollingService.remove(this.match, [MatchPollUpdateType.Status]);
    this.poll$?.unsubscribe();
  }

  private setVideos(): void {
    if (!this.match.videos_native || this.match.videos_native.length === 0) {
      return;
    }
    this.match.videos_native.forEach((video) => {
      if (video.subtype === VideoSubType.Highlight && video.url?.length) {
        this.highlightVideoDownload = video.url;
      }
      if (video.subtype === VideoSubType.Replay && video.url?.length) {
        this.replayVideoDownload = video.url;
      }
    });
  }

  private startPolling(): void {
    if (!this.live) {
      return;
    }
    const poll = this.matchPollingService.add(
      this.match, this.match.teams.home.teamid, [MatchPollUpdateType.Status]
    );
    this.poll$ = poll?.updates.subscribe((update) => {
      if (!update) {
        return;
      }
      if (update.type === MatchPollUpdateType.Status) {
        const data = update.data;
        this.match.status = data.status;
        this.setMinute(data.ui_minute);
        this.setScore(data.teams.home?.score, data.teams.away?.score);
      }
    });
  }

  private setMinute(minute: string): void {
    switch (minute) {
      case 'Ende reguläre Spielzeit':
      case 'Verlängerung':
        minute = 'VL';
        break;
      case 'Pause Verlängerung':
        minute = 'Pause VL';
        break;
      case 'Elfmeterschießen':
        minute = 'ELF'
        break;
      case 'Verlängerung Halbzeit':
        minute = 'VL HZ'
        break;
      case 'Halbzeit':
        minute = 'HZ'
        break;
    }
    this.minute = minute;
  }

  private setScore(homeScore?: number, awayScore?: number): void {
    homeScore = homeScore || this.match.teams.home.score;
    awayScore = awayScore || this.match.teams.away.score;
    this.homeScore = `${homeScore === -1 ? '' : homeScore}`;
    this.awayScore = `${awayScore === -1 ? '' : awayScore}`;
    let newScore = `${homeScore === -1 ? '-' : homeScore} : ${awayScore === -1 ? '-' : awayScore}`;
    if (this.match?.scores?.ui_scores?.main_score) {
      newScore = this.match.scores.ui_scores.main_score;
    }
    this.score = newScore;
  }

  private setDate(): void {
    if (!this.match.timestamps?.start) {
      return;
    }
    const date = LibUtilsService.getShortDateString(
      this.match.timestamps.start,
      true,
      false
    );
    const parts = date.split(' ');
    this.shortDateString = parts[0];
    this.timeString = parts[1];
  }

  private setLiveStreamStatus(): void {
    const status = this.match.streaming_info?.live_status;
    switch (status) {
      case LivestreamStatus.Live: {
        this.liveStreamStatus = 'ZUM STREAM';
        this.minimalLiveStreamStatus = 'STREAM';
        this.liveStreamStatusColor = '#FF0000';
        break;
      }
      case LivestreamStatus.Replay:
      case LivestreamStatus.Ended: {
        this.liveStreamStatus = 'REPLAY';
        this.minimalLiveStreamStatus = 'REPLAY';
        this.liveStreamStatusColor = '#517BBB';
        break;
      }
      case LivestreamStatus.Highlights: {
        this.liveStreamStatus = 'HIGHLIGHTS';
        this.minimalLiveStreamStatus = 'HIGHLIGHTS';
        this.liveStreamStatusColor = '#9747FF';
        break;
      }
      case LivestreamStatus.Upcoming: {
        this.minimalLiveStreamStatus = 'STREAM'
        this.liveStreamStatusColor = 'rgba(255, 0, 0, 0.7)';
        break;
      }
    }
  }

  public get matchInfoLeft(): string {
    if (this.minimalLiveStreamStatus) {
      return null;
    }
    if (this.match.isPostGameOrEnded() && this.match.isToday()) {
      return 'ENDSTAND';
    }
    if (this.minute) {
      return 'LIVE';
    }
    return this.timeString;
  }

  public get matchInfoRight(): string {
    if (this.minute) {
      return this.minute;
    }
    if (this.minimalLiveStreamStatus) {
      return this.match.isToday() ? this.timeString : this.shortDateString;
    }
    return this.shortDateString;
  }
}
